import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { api } from '../lib/api.js'
import { format, parseISO } from 'date-fns'
import { History, Filter, Search, Calendar, User, Activity, ArrowRight, FileText, Eye, AlertTriangle, Server, X } from 'lucide-react'

const ENTITIES = [
  'All', 'scope', 'process', 'asset', 'risk', 'control', 'clause',
  'internal_audit', 'finding', 'nc', 'document', 'training', 'recycle_bin', 'auth'
]

const ACTION_LABELS = {
  create: 'Created',
  update: 'Updated',
  delete: 'Deleted',
  restore: 'Restored',
  login: 'Logged In',
  upload: 'Uploaded',
  review: 'Reviewed',
  assign: 'Assigned',
  bulk_create: 'Bulk Imported',
  delete_action: 'Deleted'
}

function LogDetailsModal({ log, onClose }) {
  if (!log) return null

  let detailsObj = {}
  try {
    detailsObj = log.details ? JSON.parse(log.details) : {}
  } catch (e) {
    detailsObj = { raw: log.details }
  }

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal" style={{ maxWidth: 600 }}>
        <div className="modal-header">
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{ background: 'var(--accent)22', color: 'var(--accent)', padding: 8, borderRadius: 8 }}>
              <History size={18} />
            </div>
            <div>
              <h2 style={{ margin: 0 }}>Event Details</h2>
              <p style={{ margin: 0, fontSize: '0.75rem', color: 'var(--text-secondary)' }}>Log ID: {log.id}</p>
            </div>
          </div>
          <button className="btn btn-ghost" onClick={onClose}><X size={20} /></button>
        </div>
        <div className="modal-body">
          <div className="grid-2" style={{ marginBottom: 24 }}>
            <div>
              <label className="form-label">Timestamp</label>
              <div style={{ fontSize: '0.875rem' }}>{format(parseISO(log.created_at), 'dd MMM yyyy, HH:mm:ss')}</div>
            </div>
            <div>
              <label className="form-label">Performed By</label>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <div style={{ width: 20, height: 20, borderRadius: '50%', background: 'var(--accent)', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.625rem' }}>
                  {log.changed_by.charAt(0)}
                </div>
                <span style={{ fontWeight: 600 }}>{log.changed_by}</span>
              </div>
            </div>
            <div>
              <label className="form-label">Event Source</label>
              <span className="badge badge-neutral" style={{ textTransform: 'capitalize' }}>{log.entity.replace('_', ' ')}</span>
            </div>
            <div>
              <label className="form-label">Action</label>
              <span style={{ fontWeight: 700, color: 'var(--accent)', textTransform: 'uppercase' }}>{ACTION_LABELS[log.action] || log.action}</span>
            </div>
          </div>

          <label className="form-label">Detailed Context / JSON Data</label>
          <pre style={{
            background: 'var(--bg-base)',
            padding: 16,
            borderRadius: 8,
            fontSize: '0.75rem',
            overflowX: 'auto',
            border: '1px solid var(--border)',
            color: 'var(--accent)',
            fontFamily: 'monospace'
          }}>
            {JSON.stringify(detailsObj, null, 2)}
          </pre>
        </div>
        <div className="modal-footer">
          <button className="btn btn-primary" onClick={onClose}>Close Details</button>
        </div>
      </div>
    </div>
  )
}

export function AuditLogs() {
  const [filters, setFilters] = useState({
    entity: 'All',
    from: '',
    to: ''
  })
  const [selectedLog, setSelectedLog] = useState(null)

  const { data: logs = [], isLoading } = useQuery({
    queryKey: ['audit-logs', filters],
    queryFn: () => api.getAuditLogs(filters),
    refetchInterval: 30000 // Refresh every 30s
  })

  const getEntityIcon = (entity) => {
    switch (entity) {
      case 'risk': return <AlertTriangle size={14} color="var(--danger)" />
      case 'asset': return <Server size={14} color="var(--accent)" />
      case 'auth': return <User size={14} color="var(--success)" />
      case 'document': return <FileText size={14} color="var(--warning)" />
      default: return <Activity size={14} color="var(--text-muted)" />
    }
  }

  const parseDetails = (log) => {
    if (!log.details) return 'No details recorded'
    try {
      const d = JSON.parse(log.details)
      if (d.changes) {
        return (
          <ul style={{ margin: 0, paddingLeft: 16, fontSize: '0.75rem' }}>
            {d.changes.map((c, i) => <li key={i}>{c}</li>)}
          </ul>
        )
      }
      if (d.name || d.title || d.description) {
        return <span>Item: {d.name || d.title || d.description}</span>
      }
      if (d.reason) {
        return <span>Reason: {d.reason}</span>
      }
      return JSON.stringify(d)
    } catch (e) {
      return log.details
    }
  }

  return (
    <div className="page">
      <div className="page-header">
        <div>
          <h1 className="page-title">Global Audit Logs</h1>
          <p className="page-subtitle">Historical trail of all system activities and compliance events</p>
        </div>
      </div>

      <div className="filter-bar" style={{ marginBottom: 24, padding: 16, background: 'var(--bg-elevated)', borderRadius: 12, display: 'flex', gap: 20, alignItems: 'flex-end', border: '1px solid var(--border)' }}>
        <div style={{ flex: 1 }}>
          <label style={{ fontSize: '0.75rem', fontWeight: 700, color: 'var(--text-secondary)', marginBottom: 8, display: 'block', textTransform: 'uppercase' }}>
            <Filter size={12} style={{ marginRight: 6 }} /> Entity / Page
          </label>
          <select
            className="form-select"
            value={filters.entity}
            onChange={e => setFilters(f => ({ ...f, entity: e.target.value }))}
          >
            {ENTITIES.map(e => <option key={e} value={e}>{e === 'All' ? 'All Activities' : e.charAt(0).toUpperCase() + e.slice(1).replace('_', ' ')}</option>)}
          </select>
        </div>

        <div>
          <label style={{ fontSize: '0.75rem', fontWeight: 700, color: 'var(--text-secondary)', marginBottom: 8, display: 'block', textTransform: 'uppercase' }}>
            <Calendar size={12} style={{ marginRight: 6 }} /> From Date
          </label>
          <input
            type="date"
            className="form-input"
            value={filters.from}
            onChange={e => setFilters(f => ({ ...f, from: e.target.value }))}
          />
        </div>

        <div>
          <label style={{ fontSize: '0.75rem', fontWeight: 700, color: 'var(--text-secondary)', marginBottom: 8, display: 'block', textTransform: 'uppercase' }}>
            <Calendar size={12} style={{ marginRight: 6 }} /> To Date
          </label>
          <input
            type="date"
            className="form-input"
            value={filters.to}
            onChange={e => setFilters(f => ({ ...f, to: e.target.value }))}
          />
        </div>

        <button
          className="btn btn-secondary"
          onClick={() => setFilters({ entity: 'All', from: '', to: '' })}
        >
          Reset Filters
        </button>
      </div>

      <div className="table-wrapper shadow-sm">
        <table className="audit-table">
          <thead>
            <tr>
              <th style={{ width: 180 }}>Timestamp</th>
              <th style={{ width: 140 }}>Event Source</th>
              <th style={{ width: 100 }}>Entity ID</th>
              <th style={{ width: 120 }}>Action</th>
              <th style={{ width: 150 }}>Performed By</th>
              <th>Details / Changes</th>
              <th style={{ width: 80 }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr><td colSpan="7"><div className="empty-state">Loading logs...</div></td></tr>
            ) : logs.length === 0 ? (
              <tr><td colSpan="7"><div className="empty-state">No audit logs found for the selected criteria.</div></td></tr>
            ) : logs.map(log => (
              <tr key={log.id}>
                <td style={{ fontSize: '0.8125rem', whiteSpace: 'nowrap' }}>
                  <div style={{ fontWeight: 600 }}>{format(parseISO(log.created_at), 'dd MMM yyyy')}</div>
                  <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>{format(parseISO(log.created_at), 'HH:mm:ss')}</div>
                </td>
                <td>
                  <span className="badge badge-neutral" style={{ textTransform: 'capitalize', display: 'flex', alignItems: 'center', gap: 6, width: 'fit-content' }}>
                    {log.entity.replace('_', ' ')}
                  </span>
                </td>
                <td style={{ fontFamily: 'monospace', fontSize: '0.75rem', color: 'var(--text-muted)' }}>
                  #{log.entity_id}
                </td>
                <td>
                  <span style={{
                    fontWeight: 700,
                    fontSize: '0.75rem',
                    color: log.action === 'delete' ? 'var(--danger)' : log.action === 'create' ? 'var(--success)' : 'var(--accent)',
                    textTransform: 'uppercase'
                  }}>
                    {ACTION_LABELS[log.action] || log.action}
                  </span>
                </td>
                <td>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <div style={{ width: 24, height: 24, borderRadius: '50%', background: 'var(--accent)22', color: 'var(--accent)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.625rem', fontWeight: 800 }}>
                      {log.changed_by.charAt(0)}
                    </div>
                    <span style={{ fontSize: '0.8125rem', fontWeight: 500 }}>{log.changed_by}</span>
                  </div>
                </td>
                <td>
                  <div style={{ fontSize: '0.8125rem', color: 'var(--text-secondary)', maxWidth: 400, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {parseDetails(log)}
                  </div>
                </td>
                <td>
                  <button className="btn btn-ghost btn-sm" onClick={() => setSelectedLog(log)} title="View Details">
                    <Eye size={16} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <LogDetailsModal log={selectedLog} onClose={() => setSelectedLog(null)} />

      <style dangerouslySetInnerHTML={{
        __html: `
        .audit-table td {
          vertical-align: top;
          padding: 16px 12px;
        }
        .audit-table tr:hover {
          background: var(--bg-elevated)44;
        }
      `}} />
    </div>
  )
}
